import React, { useState, useEffect, useContext } from 'react';
import { Box, Flex, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { FaInstagram } from 'react-icons/fa';
import { isVoided } from '@foodfluence/shared';
import { useNavigate, useLocation } from 'react-router-dom';
import deliverableNames from '../../../utils/deliverableNames';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import InputLinkModal from '../components/InputLinkModal';
import PendingReviewModal from '../components/ContentUnderReviewModal';
import { ClientDataContext } from 'contexts/ClientDataContext';
// ConversationsContext removed — not used on this page
import DraftUploadModal from '../components/DraftUploadModal';
import HelpSection from '../components/HelpSection';
import StorySubmissionModal from '../components/story-submission-flow';
import CompletedModal from '../components/CompletedContentModal';
import { getInfluencerCollabPhase, linkNeedsEdits } from 'utils/influencerCollabPhase';
import { activeLinks } from '@foodfluence/shared';
import SubmissionPhaseIndicator from '../components/SubmissionPhaseIndicator';
import DeliverableCard from '../components/DeliverableCard';
import {
  EditsBanner,
  ShopifyCodeBox,
  SubmissionEmpty,
  SubmissionGroups,
  SubmissionHeader,
  SUBMISSION_MAX_W,
} from '../components/SubmissionPageParts';
import { deliverableCardStatus } from '../utils/submissionCards';
import { ff } from '../../../theme';

const DeliverableSubmissionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const collabId = queryParams.get('id');

  const collabData = useContext(CollabDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const collab = collabData.find(c => c.id === collabId);
  const campaign = campaignData[collabId!];
  const clients = useContext(ClientDataContext);
  const client = clients[campaign?.clientID];

  const [deliverableLinks, setDeliverableLinks] = useState<{ [key: string]: any[] }>({});
  const [currentDeliverable, setCurrentDeliverable] = useState<{ key: string, index: number } | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
  const [currentPendingLink, setCurrentPendingLink] = useState('');
  const [isCompletedModalOpen, setIsCompletedModalOpen] = useState(false);
  const [completedDeliverableName, setCompletedDeliverableName] = useState('');
  const [completedContentUrl, setCompletedContentUrl] = useState('');
  const [completedPostLink, setCompletedPostLink] = useState('');
  const { isOpen: isDraftModalOpen, onOpen: onDraftModalOpen, onClose: onDraftModalClose } = useDisclosure();
  const { isOpen: isLinkModalOpen, onOpen: onLinkModalOpen, onClose: onLinkModalClose } = useDisclosure();
  const { isOpen: isStorySubmissionModalOpen, onOpen: onStorySubmissionModalOpen, onClose: onStorySubmissionModalClose } = useDisclosure();
  const [reviewContentType, setReviewContentType] = useState<'link' | 'file'>('link');
  const [reviewStage, setReviewStage] = useState<'brand' | 'foodfluence'>('foodfluence');
  const [pendingDeliverableKey, setPendingDeliverableKey] = useState('');
  const [pendingSubmittedAt, setPendingSubmittedAt] = useState<Date | null>(null);

  const toast = useToast();

  useEffect(() => {
    if (collab && collab.deliverableLinks) {
      setDeliverableLinks(collab.deliverableLinks);
    }
  }, [collab]);

  const sortedDeliverables = Object.entries(deliverableLinks)
    .filter(([_, links]) => links.length > 0)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

  const phase = getInfluencerCollabPhase(deliverableLinks);

  const handlePostButtonClick = (key: string, index: number, showFeedback: boolean = false) => {
    if (deliverableLinks[key][index].status === 'pendingVerification') {
      setIsPendingModalOpen(true);
      setReviewStage('foodfluence');
      setPendingDeliverableKey(key);
      if (deliverableLinks[key][index].userLink) {
        setReviewContentType('link');
        setCurrentPendingLink(deliverableLinks[key][index].userLink);
      } else {
        setReviewContentType('file');
        setCurrentPendingLink(deliverableLinks[key][index].verifiedUGCLink);
      }
    } else if (deliverableLinks[key][index].status === 'approved') {
      const link = deliverableLinks[key][index];
      setCompletedDeliverableName(deliverableNames[key as keyof typeof deliverableNames]?.singular);
      // Prefer the verified copy of what actually went live, falling back to
      // the approved draft, so finished work stays reviewable.
      setCompletedContentUrl(link.verifiedUGCLink || link.draftUrl || '');
      setCompletedPostLink(link.userLink || '');
      setIsCompletedModalOpen(true);
    } else if (deliverableLinks[key][index].status === 'pendingDraftSubmission') {
      setCurrentDeliverable({ key, index });
      setShowFeedback(showFeedback);
      onDraftModalOpen();
    } else if (deliverableLinks[key][index].status === 'pendingSubmission') {
      setCurrentDeliverable({ key, index });
      setShowFeedback(showFeedback);
      if (key === 'instagramStories') {
        onStorySubmissionModalOpen();
      } else {
        onLinkModalOpen();
      }
    }
  };

  const handleCloseModal = () => {
    if (isDraftModalOpen) {
      onDraftModalClose();
    } else if (isStorySubmissionModalOpen) {
      onStorySubmissionModalClose();
    } else if (isLinkModalOpen) {
      onLinkModalClose();
    }
    setCurrentDeliverable(null);
    setShowFeedback(false);
  };

  const handleReviewClick = (link: any, key: string, _index: number) => {
    setReviewStage('brand');
    setReviewContentType('file');
    setCurrentPendingLink(link.draftUrl);
    setPendingDeliverableKey(key);
    setPendingSubmittedAt(link.draftSubmittedAt?.toDate?.() ?? null);
    setIsPendingModalOpen(true);
  };

  const handleCardClick = (key: string, index: number) => {
    const link = deliverableLinks[key][index];
    if (link.status === 'needsInfluencerEdit') {
      setCurrentDeliverable({ key, index });
      setShowFeedback(true);
      onDraftModalOpen();
    } else if (link.status === 'pendingDraftReview') {
      handleReviewClick(link, key, index);
    } else {
      handlePostButtonClick(key, index, !!link.rejectionReason);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard', status: 'success', duration: 2000, isClosable: true });
  };

  if (!collab) {
    return <div>Loading...</div>;
  }

  const needsEditCount = activeLinks(deliverableLinks).filter(linkNeedsEdits).length;

  const deliverableCards: { key: string; index: number }[] = [];

  // Voided slots stay in the list — DeliverableCard renders them dimmed and unclickable, so the
  // creator sees the brand withdrew one rather than silently finding one fewer. `index` is the true
  // position in `deliverableLinks[key]`, which is what every handler and submission write addresses.
  sortedDeliverables.forEach(([key, links]) => {
    links.forEach((_link, index) => {
      deliverableCards.push({ key, index });
    });
  });

  const groupOf = ({ key, index }: { key: string; index: number }) => {
    const link = deliverableLinks[key][index];
    const voided = isVoided(link);
    return deliverableCardStatus(link.status, { voided, needsEdits: !voided && linkNeedsEdits(link) }).group;
  };
  const todoCount = deliverableCards.filter(c => groupOf(c) === 'todo').length;
  const reviewCount = deliverableCards.filter(c => groupOf(c) === 'review').length;
  const brandName = client?.accountName || 'The brand';

  return (
    <Box minH="100svh" bg="white" fontFamily="display" color={ff.ink}>
      <Box maxW={SUBMISSION_MAX_W} mx="auto" pb="32px">
        <SubmissionHeader
          eyebrow={client?.accountName ? `Collab x ${client.accountName}` : 'Collab'}
          title="Submit posts"
          onBack={() => navigate(-1)}
          client={client}
        />

        <Flex px="20px" pt="18px" direction="column" gap="18px">
          {/* Phase indicator */}
          <Box>
            <SubmissionPhaseIndicator
              phase={phase}
              requireDraftApproval={campaign?.requireDraftApproval}
            />
            <Text mt="8px" fontSize="14px" color={ff.body} lineHeight="normal">
              {deliverableCards.length > 0 ? `${todoCount} to do · ${reviewCount} in review` : 'Nothing due yet.'}
            </Text>
          </Box>

          {/* Edits needed alert */}
          {needsEditCount > 0 && (
            <EditsBanner
              title={`${needsEditCount} ${needsEditCount === 1 ? 'edit' : 'edits'} requested`}
              body="Tap the red cards below to see the feedback, then resubmit."
            />
          )}

          {/* Help Section */}
          <HelpSection collab={collab} campaign={campaign} client={client} />

          <ShopifyCodeBox collab={collab} campaign={campaign} client={client} onCopy={handleCopy} />

          {/* Deliverables — voided slots stay listed (dimmed) under Done */}
          {deliverableCards.length > 0 ? (
            <SubmissionGroups
              items={deliverableCards}
              groupOf={groupOf}
              render={({ key, index }) => (
                <DeliverableCard
                  key={`${key}-${index}`}
                  deliverableKey={key}
                  index={index}
                  link={deliverableLinks[key][index]}
                  brandName={brandName}
                  onClick={() => handleCardClick(key, index)}
                />
              )}
            />
          ) : (
            <SubmissionEmpty icon={FaInstagram} title="No posts to submit yet" body="No deliverables for this collab." />
          )}
        </Flex>
      </Box>

      {/* Submission & review modals */}
      <PendingReviewModal
        isOpen={isPendingModalOpen}
        onClose={() => setIsPendingModalOpen(false)}
        contentUrl={currentPendingLink}
        contentType={reviewContentType}
        reviewStage={reviewStage}
        deliverableKey={pendingDeliverableKey}
        submittedAt={pendingSubmittedAt}
      />

      <CompletedModal
        isOpen={isCompletedModalOpen}
        onClose={() => setIsCompletedModalOpen(false)}
        title="Deliverable Completed!"
        subtitle={`Your ${completedDeliverableName} has been completed and verified.`}
        body="Keep up the good work and continue with your other deliverables."
        contentUrl={completedContentUrl || undefined}
        postLink={completedPostLink || undefined}
      />

      <StorySubmissionModal
        isOpen={isStorySubmissionModalOpen}
        onClose={handleCloseModal}
        deliverableKey={currentDeliverable?.key || ''}
        linkIndex={currentDeliverable?.index || 0}
        showFeedback={showFeedback}
        rejectionReason={currentDeliverable ? deliverableLinks[currentDeliverable.key]?.[currentDeliverable.index]?.rejectionReason : null}
        clientName={client?.accountName || ''}
        campaign={campaign}
        clientId={client?.id || ''}
        approvedContentUrl={currentDeliverable ? deliverableLinks[currentDeliverable.key]?.[currentDeliverable.index]?.draftUrl : undefined}
      />

      <InputLinkModal
        isOpen={isLinkModalOpen}
        onClose={handleCloseModal}
        deliverableKey={currentDeliverable?.key || ''}
        postLink={currentDeliverable ? deliverableLinks[currentDeliverable.key]?.[currentDeliverable.index]?.userLink : ''}
        linkIndex={currentDeliverable?.index || 0}
        showFeedback={showFeedback}
        rejectionReason={currentDeliverable ? deliverableLinks[currentDeliverable.key]?.[currentDeliverable.index]?.rejectionReason : null}
        modalHeader={currentDeliverable ? `${deliverableNames[currentDeliverable.key as keyof typeof deliverableNames]?.singular} #${currentDeliverable.index + 1}` : ''}
        clientName={client?.accountName || ''}
        clientId={client?.id || ''}
        approvedContentUrl={currentDeliverable ? deliverableLinks[currentDeliverable.key]?.[currentDeliverable.index]?.draftUrl : undefined}
      />

      <DraftUploadModal
        isOpen={isDraftModalOpen}
        onClose={handleCloseModal}
        deliverableKey={currentDeliverable?.key || ''}
        linkIndex={currentDeliverable?.index || 0}
        showFeedback={showFeedback}
        rejectionReason={currentDeliverable ? (deliverableLinks[currentDeliverable.key]?.[currentDeliverable.index]?.feedback || deliverableLinks[currentDeliverable.key]?.[currentDeliverable.index]?.rejectionReason) : null}
        feedbackPdfUrl={
          currentDeliverable
            ? deliverableLinks[currentDeliverable.key]?.[currentDeliverable.index]?.feedbackPDF ?? null
            : null
        }
        modalHeader={currentDeliverable ? `${deliverableNames[currentDeliverable.key as keyof typeof deliverableNames]?.singular} #${currentDeliverable.index + 1}` : ''}
        clientName={client?.accountName || ''}
        campaign={campaign}
        existingContentUrl={
          showFeedback && currentDeliverable
            ? deliverableLinks[currentDeliverable.key]?.[currentDeliverable.index]?.draftUrl
            : undefined
        }
      />
    </Box>
  );
};

export default DeliverableSubmissionPage;
